import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import { AGENDA_TEAMS, ATTENDING_TEAMS } from '../utils/constants';

const AddAgendaForm = ({ onSubmit, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: '',
    location: '',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd'),
    time: '09:00',
    endTime: '10:00',
    team: 'umum',
    attendingTeam: 'kepala'
  });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialData) {
      // Trim seconds from endTime if present (e.g. "10:00:00" -> "10:00")
      let formattedEndTime = '10:00';
      if (initialData.endTime !== null && initialData.endTime !== undefined) {
        formattedEndTime = initialData.endTime.length >= 5 ? initialData.endTime.substring(0, 5) : initialData.endTime;
      }
      setFormData({
        title: initialData.title || '',
        location: initialData.location || '',
        description: initialData.description || '',
        date: initialData.date ? format(new Date(initialData.date), 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd'),
        time: initialData.time || '09:00',
        endTime: formattedEndTime,
        team: initialData.team || 'umum',
        attendingTeam: initialData.attendingTeam || 'kepala'
      });
    }
  }, [initialData]);

  // Handle Escape key press
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === 'Escape') {
        if (onCancel) {
          onCancel();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [onCancel]);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    // Description is now optional, so no validation error for empty description
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData.time) {
      newErrors.time = 'Time is required';
    }
    if (!formData.endTime) {
      newErrors.endTime = 'End Time is required';
    }
    if (!formData.team) {
      newErrors.team = 'Team selection is required';
    }
    if (!formData.attendingTeam) {
      newErrors.attendingTeam = 'Attending team selection is required';
    }
    // Validate that time is before endTime
    if (formData.time && formData.endTime) {
      const [startHour, startMinute] = formData.time.split(':').map(Number);
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);
      if (startHour > endHour || (startHour === endHour && startMinute >= endMinute)) {
        newErrors.time = 'Start Time must be before End Time';
      }
    }
    // Validate endTime does not exceed 23:59
    if (formData.endTime) {
      const [endHour, endMinute] = formData.endTime.split(':').map(Number);
      if (endHour > 23 || (endHour === 23 && endMinute > 59)) {
        newErrors.endTime = 'End Time cannot exceed 23:59';
      }
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        await onSubmit(formData);
      } catch (error) {
        setErrors({ submit: initialData ? 'Failed to update agenda. Please try again.' : 'Failed to create agenda. Please try again.' });
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when field is edited
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.submit && (
        <div className="text-sm text-red-600 mb-4">
          {errors.submit}
        </div>
      )}



      <div>
        <label htmlFor="title" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
          Nama Kegiatan
        </label>
        <input
          type="text"
          id="title"
          name="title"
          value={formData.title}
          onChange={handleChange}
          placeholder="Enter agenda title"
          className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.title ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
            }`}
        />
        {errors.title && (
          <p className="mt-1 text-sm text-red-600">{errors.title}</p>
        )}
      </div>

      <div>
        <label htmlFor="location" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
          Tempat Kegiatan
        </label>
        <input
          type="text"
          id="location"
          name="location"
          value={formData.location}
          onChange={handleChange}
          placeholder="Masukkan tempat kegiatan"
          className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300`}
        />
      </div>

      <div>
        <label htmlFor="description" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
          Deskripsi Kegiatan
        </label>
        <textarea
          id="description"
          name="description"
          rows="4"
          value={formData.description}
          onChange={handleChange}
          placeholder="Enter agenda description"
          className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.description ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
            }`}
        />
        {errors.description && (
          <p className="mt-1 text-sm text-red-600">{errors.description}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        <div>
          <label htmlFor="date" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
            Tanggal
          </label>
          <input
            type="date"
            id="date"
            name="date"
            value={formData.date}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.date ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
              }`}
          />
          {errors.date && (
            <p className="mt-1 text-sm text-red-600">{errors.date}</p>
          )}
        </div>

        <div>
          <label htmlFor="time" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
            Waktu Mulai
          </label>
          <input
            type="time"
            id="time"
            name="time"
            value={formData.time}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.time ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
              }`}
          />
          {errors.time && (
            <p className="mt-1 text-sm text-red-600">{errors.time}</p>
          )}
        </div>

        <div>
          <label htmlFor="endTime" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
            Waktu Selesai
          </label>
          <input
            type="time"
            id="endTime"
            name="endTime"
            value={formData.endTime}
            onChange={handleChange}
            className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.endTime ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
              }`}
          />
          {errors.endTime && (
            <p className="mt-1 text-sm text-red-600">{errors.endTime}</p>
          )}
        </div>
      </div>

      <div>
        <label htmlFor="team" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
          Tim yang Mengajukan
        </label>
        <select
          id="team"
          name="team"
          value={formData.team}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.team ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
            }`}
        >
          <option value="" disabled>Pilih Tim yang Mengajukan</option>
          {Object.entries(AGENDA_TEAMS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        {errors.team && (
          <p className="mt-1 text-sm text-red-600">{errors.team}</p>
        )}
      </div>
      <div>
        <label htmlFor="attendingTeam" className="block text-[13px] font-medium text-slate-700 tracking-wide uppercase mb-1.5">
          Tim yang Menghadiri
        </label>
        <select
          id="attendingTeam"
          name="attendingTeam"
          value={formData.attendingTeam}
          onChange={handleChange}
          className={`mt-1 block w-full rounded-xl border border-slate-200/60 bg-white/50 backdrop-blur-sm px-4 py-2.5 text-slate-800 focus:outline-none focus:border-slate-400 focus:ring-4 focus:ring-slate-100 transition-all duration-300 ${errors.attendingTeam ? 'border-red-500 focus:ring-red-100 focus:border-red-400' : ''
            }`}
        >
          <option value="" disabled>Pilih Tim yang Menghadiri</option>
          {Object.entries(ATTENDING_TEAMS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        {errors.attendingTeam && (
          <p className="mt-1 text-sm text-red-600">{errors.attendingTeam}</p>
        )}
      </div>

      <div className="flex justify-end space-x-4 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className="px-6 py-2.5 border border-slate-200/60 rounded-full text-sm font-medium text-slate-600 bg-white/50 hover:bg-white hover:text-slate-800 focus:outline-none transition-all duration-300"
        >
          Batal
        </button>
        <button
          type="submit"
          className="px-6 py-2.5 border border-transparent rounded-full shadow-sm shadow-slate-800/20 text-sm font-medium text-white bg-slate-800 hover:bg-slate-700 hover:scale-[1.02] focus:outline-none transition-all duration-300"
        >
          {initialData ? 'Update Agenda' : 'Tambah Agenda'}
        </button>
      </div>
    </form>
  );
};

export default AddAgendaForm;
